import { CarbonLabUserWsMessage } from '@cibo/core'
import { AuthUserPermission, usePermissions } from '@cibo/profile'
import ClearIcon from '@mui/icons-material/Clear'
import { Button, IconButton } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useParams } from 'react-router'
import { useNavigate } from 'react-router-dom'
import { useActiveJobs } from '../queries/useActiveJobs'

interface JobToastProps {
  suppressPaths?: string[]
  CARBON_LAB_ROUTES: {
    LAND_DETAIL_SUBROUTE_CARBON_LAB: string
    LAND_DETAIL: string
    CARBON_LAB_JOB: string
  }
}

export const JobToast = ({ suppressPaths, CARBON_LAB_ROUTES }: JobToastProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation('jobToast')
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { can } = usePermissions()
  const [consumedMessages, setConsumedMessages] = useState<string[]>([])

  const { completedJob } = useActiveJobs()

  const { jobId } = useParams()
  const suppress = jobId ? completedJob?.entityIds.includes(jobId) : false

  const goToCarbonLab = (job: CarbonLabUserWsMessage) => {
    navigate(generatePath(CARBON_LAB_ROUTES.CARBON_LAB_JOB, { jobId: job.details.jobId }))
  }

  if (
    completedJob &&
    !consumedMessages.includes(completedJob.messageId) &&
    can({ useAny: AuthUserPermission.CARBON_LAB })
  ) {
    if (!suppress) {
      enqueueSnackbar(
        `${t('carbonLab', { ns: 'privatelabel' })}: ${t('message', {
          context: completedJob.details.status,
        })}`,
        {
          variant:
            completedJob.details.status === 'success'
              ? 'success'
              : ['processing', 'queued'].includes(completedJob.details.status)
              ? 'info'
              : 'warning',
          action: key =>
            completedJob.details.status === 'success' ||
            completedJob.details.status === 'failed' ? (
              <>
                <Button
                  sx={{ color: 'inherit' }}
                  onClick={() => {
                    closeSnackbar(key)
                    goToCarbonLab(completedJob)
                  }}
                >
                  {t('cta', { context: completedJob.details.status })}
                </Button>
                <IconButton
                  aria-label={t('dismiss')}
                  sx={{ color: 'inherit' }}
                  onClick={() => {
                    closeSnackbar(key)
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </>
            ) : (
              <IconButton
                aria-label={t('dismiss')}
                sx={{ color: 'inherit' }}
                onClick={() => {
                  closeSnackbar(key)
                }}
              >
                <ClearIcon />
              </IconButton>
            ),
        }
      )
    }
    setConsumedMessages([...consumedMessages, completedJob.messageId])
  }

  return null
}
